import React, { createContext, useContext, useEffect, useState } from 'react';

import { localizeError } from '../FirebaseContext/utils';
import {
  EmailAuthProvider,
  onAuthStateChanged,
  persistence,
  reauthenticateWithCredential,
  setPersistence,
  signInWithCustomToken,
  signInWithEmailAndPassword,
  signOut,
  updatePassword as updateFirebasePassword,
} from './firebaseMethods';
import type {
  AuthProviderProps,
  Credentials,
  IAuthContext,
  PeristenceHandler,
  PersistenceHandlerType,
  TokenType,
  User,
} from './interface';

// We case so Typescript won't complain. We want the default provider to fail. We require an auth instance.
export const AuthContext = createContext<IAuthContext>(null as unknown as IAuthContext);
export const useAuthContext = () => useContext(AuthContext);

export const AuthProvider = (props: AuthProviderProps): React.ReactElement => {
  const { auth, children, token, tokenType, testUser, onTokenError, onInitError } = props;
  const [user, setUser] = useState<User | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [isImpersonating, setIsImpersonating] = useState(false);

  const [persistenceHandlerType, persistenceHandler] = determinePersistence();

  // Run only on mount for initial authentication logic (i.e. tokens for auth)
  // This means if the auth prop ever changes (realistically it shouldn't)
  // None of the flow in this use effect will utilize the new auth prop
  useEffect(() => {
    initializeUser();

    const unsubscribeFromAuth = onAuthStateChanged(auth, (user) => {
      setUser(user);
    });

    return () => {
      unsubscribeFromAuth();
    };
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user: testUser || user,
        isLoading: !isReady,
        isReady,
        isImpersonating,
        login,
        loginWithToken,
        logout,
        updatePassword,
        reauthenticateUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );

  // Determine the token persistence strategy based by the tokenType or previous application state.
  function determinePersistence(): [PersistenceHandlerType, PeristenceHandler | null | undefined] {
    const persistencehandler = persistence['impersonate'];

    // application knows to use impersonation (e.g. on initial load with token)
    const isImpersonationToken = tokenType === 'impersonate';

    // check if user was previously impersonating and the application is no longer aware of it (e.g. on browser refresh)
    const wasPreviouslyImpersonating = persistencehandler?.getItem('isImpersonating') === 'true';

    if (isImpersonationToken || (!tokenType && wasPreviouslyImpersonating)) {
      return ['impersonate', persistencehandler];
    }

    return ['default', persistence['default']];
  }

  async function initializeUser() {
    try {
      // Mobile doesn't currently use setPersistence
      if (persistenceHandler) {
        await setPersistence(auth, persistenceHandler.type);
      }
    } catch (e: any) {
      onInitError?.('set-persistence', e);
    }

    // If there is a token, we need to end the current session (if there is one)
    // Then re-authenticate the user before the app loads
    if (token) {
      try {
        await logout();
      } catch (e: any) {
        onInitError?.('pre-token-authentication', e);
      }

      try {
        const user = await loginWithToken(token, tokenType);
        setUser(user);
      } catch (e: any) {
        onTokenError(e, tokenType);
        onInitError?.('token-authentication', e);
      }
    }

    if (persistenceHandlerType === 'impersonate') {
      setIsImpersonating(true);
    }

    setIsReady(true);
  }

  async function login(credentials: Credentials) {
    try {
      const result = await signInWithEmailAndPassword(
        auth,
        credentials.email,
        credentials.password,
      );
      setIsImpersonating(false);
      persistenceHandler?.setItem('isImpersonating', 'false');
      return result.user;
    } catch (error: any) {
      const localizedError = localizeError(error);
      throw localizedError;
    }
  }

  async function loginWithToken(token: string, tokenType: TokenType) {
    const { user } = await signInWithCustomToken(auth, token);
    setIsImpersonating(tokenType === 'impersonate');
    persistenceHandler?.setItem('isImpersonating', 'true');
    return user;
  }

  async function logout() {
    await signOut(auth);
    setIsImpersonating(false);
    persistenceHandler?.setItem('isImpersonating', 'false');
  }

  async function updatePassword(password: string) {
    if (!user) {
      throw new Error('Cannot update password without an authenticated user');
    }

    await updateFirebasePassword(user, password);
  }

  async function reauthenticateUser(password: string) {
    if (!user?.email) {
      throw new Error('Cannot reauthenticate user without an authenticated user and email');
    }

    const credential = EmailAuthProvider.credential(user.email, password);
    const result = await reauthenticateWithCredential(user, credential);
    return result.user;
  }
};
